/**
 * @file PageRollbackDialog.tsx
 * @description «Вернуть версию N?» страницы (phase19.16 Р3а; SB126, SB52,
 * SB72; uiux-r3 §3.1): возврат — новая публикация, версия сразу станет видна
 * покупателям; неопубликованные правки пропадут (возврат без `keepDraft`).
 * Перед подтверждением — что изменится по `rollback-preview`: пропадающие
 * правки (сколько, кто), занятый адрес, изменённый дизайн, удалённые файлы,
 * ошибки стоп-листа (возврат с ними сервер не примет). Отказ сервера (409
 * «черновик изменился» и др.) — текстом и кнопкой «Обновить»: сводка и
 * предпросмотр перечитываются (uiux-r3 §3.1). До sm — на весь экран.
 */

import { AlertCircle, RefreshCw, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { useNotify } from "../../ui/NotifyProvider";
import { useSiteView } from "../../site-view/site-view-context";
import { useBuilder } from "../builder-context";
import { sitePagePublish, type SitePageRollbackPreview } from "../../../lib/api-site-page-publish";
import { pagePublishErrorText } from "../../../lib/site-builder/page-publish-errors";
import { editorLabel, editsWord } from "../../../lib/site-builder/page-status";

/** Предупреждения возврата словами (пусто — ничего не теряется). */
function warningsOf(preview: SitePageRollbackPreview, currentUserId: string | null): string[] {
  const out: string[] = [];
  if (preview.lostDraft) {
    const who = editorLabel(preview.lostDraft.editedBy, currentUserId);
    out.push(`Неопубликованные правки (${editsWord(preview.lostDraft.count)}${who ? `, ${who}` : ""}) пропадут.`);
  }
  if (preview.slugConflict) {
    out.push(`Адрес /${preview.slugConflict.slug} теперь у другой страницы — останется адрес /${preview.slugConflict.willUse}.`);
  }
  if (preview.designChanged) out.push("Дизайн этой версии с тех пор изменили или удалили — на сайте будет дизайн, сохранённый в версии.");
  if (preview.missingMedia.length > 0) out.push(`Файлов из этой версии больше нет в медиатеке: ${preview.missingMedia.length} — на сайте их не будет.`);
  return out;
}

interface Props {
  version: number | null;
  onClose: () => void;
  /** После возврата (закрыть «Все версии» и просмотр версии). */
  onDone: () => void;
}

export function PageRollbackDialog({ version, onClose, onDone }: Props) {
  const b = useBuilder();
  const view = useSiteView();
  const notify = useNotify();
  const pageId = b.publishing.changes?.pageId ?? b.pages.page?.id ?? null;
  const [preview, setPreview] = useState<SitePageRollbackPreview | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Отказ сервера: предпросмотр устарел — «Обновить» перечитывает его и сводку черновика.
  const [stale, setStale] = useState(false);

  const loadPreview = useCallback(() => {
    setPreview(null);
    setProblem(null);
    setStale(false);
    if (version === null || !pageId) return;
    sitePagePublish
      .rollbackPreview(pageId, version)
      .then(setPreview)
      .catch((err: unknown) => setProblem(pagePublishErrorText(err, "Не удалось проверить версию. Черновик и сайт в порядке.")));
  }, [version, pageId]);

  useEffect(() => loadPreview(), [loadPreview]);

  async function refresh() {
    await b.publishing.reload();
    loadPreview();
  }

  async function onConfirm() {
    if (version === null) return;
    setBusy(true);
    try {
      await b.publishing.rollback(version);
      notify.success(`Версия ${version} возвращена и опубликована. Покупатели увидят её в течение минуты.`);
      onDone();
    } catch (err) {
      setProblem(pagePublishErrorText(err, "Не удалось вернуть версию — сайт и черновик остались прежними."));
      setStale(true);
      void b.publishing.reload();
    } finally {
      setBusy(false);
    }
  }

  const warnings = preview ? warningsOf(preview, view.currentUserId) : [];
  return (
    <Dialog open={version !== null} onOpenChange={(open) => (!open ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Вернуть версию {version}?</DialogTitle>
          <DialogDescription>
            Версия {version} сразу станет видна покупателям — это новая публикация. Вид сайта, другие страницы и то, какая страница главная, не изменятся.
          </DialogDescription>
        </DialogHeader>
        {problem ? (
          <div className="flex flex-col items-start gap-2">
            <p role="alert" className="type-body text-destructive">{problem}</p>
            {stale ? (
              <Button variant="outline" onClick={() => void refresh()}>
                <RefreshCw aria-hidden />
                Обновить
              </Button>
            ) : null}
          </div>
        ) : null}
        {!preview && !problem ? <p className="type-body text-on-surface-variant" aria-busy="true">Проверяем версию…</p> : null}
        {preview?.isLive ? <p className="type-body text-on-surface-variant">Эта версия уже на сайте.</p> : null}
        {warnings.length > 0 ? (
          <ul className="flex flex-col gap-1">
            {warnings.map((w) => (
              <li key={w} className="type-body flex items-start gap-2 text-on-surface">
                <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-warning" />
                <span className="sr-only">Внимание: </span>
                {w}
              </li>
            ))}
          </ul>
        ) : null}
        {preview && preview.checks.length > 0 ? (
          <ul className="flex flex-col gap-1">
            {preview.checks.map((c) => (
              <li key={`${c.sectionKey}:${c.path}:${c.term}`} className="type-body flex items-start gap-2 text-on-surface">
                <AlertCircle aria-hidden className="mt-0.5 size-4 shrink-0 text-destructive" />
                <span className="sr-only">Мешает возврату: </span>
                {c.message}
              </li>
            ))}
          </ul>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="brand" disabled={!preview || !preview.canRollback} loading={busy} onClick={() => void onConfirm()}>
            Вернуть эту версию
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
